import { apiClient } from './client'
import type {
  EnhancementSubmitRequest,
  EnhancementSubmitResponse,
  EnhancementSingleRequest,
  EnhancementResult,
  JobListResponse,
  JobStatusResponse,
  EnhancementResultsResponse,
  InlineEditAction,
  InlineEditResponse,
  ArticleIssuesResponse,
  IssueFeedbackRequest,
  IssueFeedbackResponse,
} from '@/types/api'

// Enhancement API service
export const enhancementsApi = {
  // Submit batch enhancement job
  async submit(request: EnhancementSubmitRequest): Promise<EnhancementSubmitResponse> {
    const response = await apiClient.post('/v1/enhancements/submit', request)
    return response.data
  },

  // Enhance single article (synchronous)
  async single(request: EnhancementSingleRequest): Promise<EnhancementResult> {
    const response = await apiClient.post('/v1/enhancements/single', request)
    return response.data
  },

  // List jobs
  async listJobs(params?: {
    page?: number
    limit?: number
    status?: string
    my_jobs_only?: boolean
  }): Promise<JobListResponse> {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.status) queryParams.append('status', params.status)
    if (params?.my_jobs_only) queryParams.append('my_jobs_only', 'true')

    const response = await apiClient.get(`/v1/enhancements/jobs?${queryParams}`)
    return response.data
  },

  // Get job status
  async getJobStatus(jobId: string): Promise<JobStatusResponse> {
    const response = await apiClient.get(`/v1/enhancements/jobs/${jobId}`)
    return response.data
  },

  // Cancel job
  async cancelJob(jobId: string): Promise<{ message: string; job_id: string }> {
    const response = await apiClient.delete(`/v1/enhancements/jobs/${jobId}`)
    return response.data
  },

  // List results
  async listResults(params?: {
    page?: number
    limit?: number
    knowledge_base?: string
    status?: string
    job_id?: string
    my_results_only?: boolean
    since_days?: number
  }): Promise<EnhancementResultsResponse> {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.knowledge_base) queryParams.append('knowledge_base', params.knowledge_base)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.job_id) queryParams.append('job_id', params.job_id)
    if (params?.my_results_only) queryParams.append('my_results_only', 'true')
    if (params?.since_days) queryParams.append('since_days', params.since_days.toString())

    const response = await apiClient.get(`/v1/enhancements/results?${queryParams}`)
    return response.data
  },

  // Search results by display_number or kb_number
  async searchResults(query: string): Promise<EnhancementResultsResponse> {
    const queryParams = new URLSearchParams()
    queryParams.append('q', query)
    const response = await apiClient.get(`/v1/enhancements/results/search?${queryParams}`)
    return response.data
  },

  // Get single result
  async getResult(displayNumber: string): Promise<{ result: EnhancementResult }> {
    const encoded = encodeURIComponent(displayNumber)
    const response = await apiClient.get(`/v1/enhancements/results/${encoded}`)
    return response.data
  },

  // Get HTML preview URL (for iframe)
  getPreviewUrl(displayNumber: string): string {
    const encoded = encodeURIComponent(displayNumber)
    return `${apiClient.defaults.baseURL}/v1/enhancements/results/${encoded}/preview`
  },

  // Get enhanced HTML as JSON
  async getHtml(displayNumber: string): Promise<{
    display_number: string
    article_title: string
    enhanced_html: string
  }> {
    const encoded = encodeURIComponent(displayNumber)
    const response = await apiClient.get(`/v1/enhancements/results/${encoded}/html`)
    return response.data
  },

  // Update enhanced HTML content
  async updateContent(displayNumber: string, enhancedHtml: string): Promise<{
    status: string
    message: string
    display_number: string
    edited_by: string
  }> {
    const encoded = encodeURIComponent(displayNumber)
    const response = await apiClient.patch(`/v1/enhancements/results/${encoded}/content`, {
      enhanced_html: enhancedHtml,
    })
    return response.data
  },

  // Update enhanced article title (ServiceNow short_description)
  async updateTitle(displayNumber: string, enhancedTitle: string): Promise<{
    status: string
    message: string
    display_number: string
    enhanced_title: string
    edited_by: string
  }> {
    const encoded = encodeURIComponent(displayNumber)
    const response = await apiClient.patch(`/v1/enhancements/results/${encoded}/title`, {
      enhanced_title: enhancedTitle,
    })
    return response.data
  },

  // Mark article as submitted to ServiceNow
  async markAsSubmitted(displayNumber: string): Promise<{
    status: string
    message: string
    display_number: string
    submitted_by: string
  }> {
    const encoded = encodeURIComponent(displayNumber)
    const response = await apiClient.post(`/v1/enhancements/results/${encoded}/mark-submitted`)
    return response.data
  },

  // Inline AI text editing
  async inlineEdit(params: {
    selectedText: string
    articleContext: string
    action: InlineEditAction
    customPrompt?: string
  }): Promise<InlineEditResponse> {
    const response = await apiClient.post('/v1/enhancements/inline-edit', {
      selected_text: params.selectedText,
      article_context: params.articleContext,
      action: params.action,
      custom_prompt: params.customPrompt,
    })
    return response.data
  },

  // ============================================================================
  // ISSUE EXPORT METHODS
  // ============================================================================

  // Export issues for a single article as Excel
  async exportArticleIssues(displayNumber: string): Promise<void> {
    const encoded = encodeURIComponent(displayNumber)
    const response = await apiClient.get(`/v1/enhancements/issues/${encoded}/export/excel`, {
      responseType: 'blob',
    })

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    const contentDisposition = response.headers['content-disposition']
    const filenameMatch = contentDisposition?.match(/filename=(.+)/)
    const filename = filenameMatch ? filenameMatch[1] : 'issues_export.xlsx'
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  },

  // Export issues for all enhanced articles (bulk) as Excel.
  // Mirrors the filters used by the results list so the export matches the view.
  async exportBulkIssues(params?: {
    knowledge_base?: string
    flag?: string
    author_name?: string
    ownership_group_name?: string
    job_id?: string
  }): Promise<void> {
    const queryParams = new URLSearchParams()
    if (params?.knowledge_base) queryParams.append('knowledge_base', params.knowledge_base)
    if (params?.flag) queryParams.append('flag', params.flag)
    if (params?.author_name) queryParams.append('author_name', params.author_name)
    if (params?.ownership_group_name) queryParams.append('ownership_group_name', params.ownership_group_name)
    if (params?.job_id) queryParams.append('job_id', params.job_id)

    const response = await apiClient.get(`/v1/enhancements/issues/export/excel?${queryParams}`, {
      responseType: 'blob',
    })

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    const contentDisposition = response.headers['content-disposition']
    const filenameMatch = contentDisposition?.match(/filename=(.+)/)
    const filename = filenameMatch ? filenameMatch[1] : 'enhancement_issues.xlsx'
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  },

  // ============================================================================
  // ISSUE FEEDBACK METHODS
  // ============================================================================

  // Get issues for an article with feedback and verification status
  async getArticleIssues(displayNumber: string): Promise<ArticleIssuesResponse> {
    const encoded = encodeURIComponent(displayNumber)
    const response = await apiClient.get(`/v1/enhancements/issues/${encoded}`)
    return response.data
  },

  // Submit feedback on an issue
  async submitFeedback(request: IssueFeedbackRequest): Promise<IssueFeedbackResponse> {
    const response = await apiClient.post('/v1/enhancements/issues/feedback', request)
    return response.data
  },

  // Delete feedback (before admin review)
  async deleteFeedback(feedbackId: number): Promise<void> {
    await apiClient.delete(`/v1/enhancements/issues/feedback/${feedbackId}`)
  },
}

export default enhancementsApi
